/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import {
  ControlLabel,
  FormControl,
  FormGroup
} from '@devstart/react-bootstrap';
import React, { CSSProperties, forwardRef, useState } from 'react';
import InputMask from 'react-input-mask';

import stripHtmlTags from '@utils/strip-html-tags';
import './input.css';

type StyleTypeProps = 'type-1' | 'type-2';

type InputProps = {
  label?: string | null;
  icon?: React.ReactNode;
  insideIcon?: React.ReactNode;
  hasError?: boolean;
  style?: CSSProperties;
  error?: string | null;
  disabled?: boolean;
  hasMask?: boolean;
  maskValue?: string;
  messageError?: string;
  inputStyleType?: StyleTypeProps;
  maxCaracters?: number;
  maxCaractersPosition?: 'left' | 'right' | 'center';
} & React.HTMLAttributes<HTMLInputElement> & { [key: string]: unknown };

const getInputStyleType = (styleType: StyleTypeProps) => {
  switch (styleType) {
    case 'type-1':
      return '';
    case 'type-2':
      return 'style-type-2';
    default:
      return '';
  }
};

const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    label,
    icon,
    hasError,
    style,
    insideIcon,
    error,
    hasMask,
    maskValue,
    messageError,
    inputStyleType = 'type-1',
    maxCaracters,
    maxCaractersPosition = 'left',
    ...rest
  },
  ref
): JSX.Element {
  const [internalValue, setInternalValue] = useState('');
  const inputError = messageError ?? error ?? (hasError ? messageError : null);

  const currentValue =
    rest.value !== undefined ? String(rest.value) : internalValue;

  const borderStyle: CSSProperties = icon
    ? { borderBottomLeftRadius: 0, borderTopLeftRadius: 0 }
    : { borderRadius: '8px' };

  if (
    (rest.name === 'effectiveDate' || rest.name === 'expirationDate') &&
    rest?.value === ''
  ) {
    borderStyle.borderColor = '#ADADAD';
  }

  const hasErrorStyle = inputError
    ? { color: '#DC3545', fontSize: '12px', fontWeight: '400' }
    : {};

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;

    // Atualiza o estado interno se não for controlado
    if (rest.value === undefined) {
      setInternalValue(newValue);
    }

    // Chama o onChange do pai se existir
    if (rest.onChange) {
      if (rest.disabled) return;
      rest.onChange(event);
    }
  };

  if (hasMask) {
    return (
      <FormGroup
        style={style}
        className={`input-container form-group ${getInputStyleType(
          inputStyleType
        )}`}
      >
        {label && <ControlLabel>{label}</ControlLabel>}
        <div className={`input-field ${insideIcon ? 'has-feedback' : ''}`}>
          {icon && (
            <span
              className='input-group-addon icon-container'
              id='basic-addon1'
            >
              {icon}
            </span>
          )}
          <InputMask
            className='form-control form-control-input'
            mask={maskValue ?? ''}
            inputRef={ref}
            style={{ ...borderStyle }}
            value={currentValue}
            {...rest}
            onChange={handleChange}
          />
          {insideIcon && (
            <div className='form-control-feedback'>{insideIcon}</div>
          )}
        </div>
        {maxCaracters && (
          <span
            className='help-message'
            style={{
              display: 'block',
              textAlign: maxCaractersPosition,
              marginTop: '-8px',
              color:
                stripHtmlTags(currentValue).length > maxCaracters
                  ? 'rgb(220, 53, 69)'
                  : ''
            }}
          >
            {stripHtmlTags(currentValue).length} / {maxCaracters} caracteres
          </span>
        )}

        {inputError && <p style={{ ...hasErrorStyle }}>{inputError}</p>}
      </FormGroup>
    );
  }

  return (
    <FormGroup
      style={style}
      className={`input-container ${getInputStyleType(inputStyleType)}`}
    >
      {label && <ControlLabel>{label}</ControlLabel>}

      <div className={`input-field ${insideIcon ? 'has-feedback' : ''}`}>
        {icon && (
          <span className='input-group-addon icon-container' id='basic-addon1'>
            {icon}
          </span>
        )}
        <FormControl
          style={{ ...borderStyle }}
          inputRef={ref}
          value={currentValue}
          {...rest}
          onChange={handleChange}
        />
        {insideIcon && (
          <div className='form-control-feedback'>{insideIcon}</div>
        )}
      </div>
      {maxCaracters && (
        <span
          className='help-message'
          style={{
            display: 'block',
            textAlign: maxCaractersPosition,
            marginTop: '-8px',
            color:
              stripHtmlTags(currentValue).length > maxCaracters
                ? 'rgb(220, 53, 69)'
                : ''
          }}
        >
          {stripHtmlTags(currentValue).length} / {maxCaracters} caracteres
        </span>
      )}

      {inputError && <p style={{ ...hasErrorStyle }}>{inputError}</p>}
    </FormGroup>
  );
});

Input.displayName = 'Input';

export default Input;
